import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { scrollToId, api } from "@/lib/api";

function Rate({ label, value, chg }) {
    const Icon = chg > 0 ? TrendingUp : chg < 0 ? TrendingDown : Minus;
    const cls = chg > 0 ? "text-emerald-400" : chg < 0 ? "text-red-400" : "text-white/40";
    return (
        <span className="flex items-center gap-x-3 gap-y-1 flex-wrap" data-testid={`rate-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <span className="text-white/40">{label}</span>
            <span className="text-white">{value != null ? `₹ ${Number(value).toLocaleString("en-IN")} / kg` : "On request"}</span>
            {chg != null && (
                <span className={`flex items-center gap-1 ${cls}`}>
                    <Icon size={11} /> {chg > 0 ? "+" : ""}{chg}%
                </span>
            )}
        </span>
    );
}

function Ticker() {
    const [data, setData] = useState(null);

    useEffect(() => {
        let live = true;
        api.get("/metal-prices")
            .then((res) => live && setData(res.data))
            .catch(() => live && setData(null));
        return () => { live = false; };
    }, []);

    return (
        <div className="border-b border-white/10" data-testid="copper-ticker">
            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-3 flex flex-wrap items-center gap-x-8 gap-y-1 font-mono text-[10px] md:text-[11px] tracking-[0.15em] uppercase">
                <Rate label="Copper" value={data?.prices?.copper} chg={data?.change?.copper} />
                <Rate label="Aluminium" value={data?.prices?.aluminium} chg={data?.change?.aluminium} />
                <Rate label="PVC Resin" value={data?.prices?.pvc} chg={null} />
                <span className="text-white/30 ml-auto hidden md:block" data-testid="ticker-timestamp">
                    {data
                        ? `Indicative · ${data.source} · USD/INR ${data.usd_inr} · as of ${new Date(data.fetched_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata" })} IST`
                        : "Fetching live rates…"}
                </span>
            </div>
        </div>
    );
}

const OFFICES = ["Bengaluru"];

const LINKS = [
    { label: "Range", id: "categories" },
    { label: "Anatomy", id: "anatomy" },
    { label: "HT Guide", id: "ht-compare" },
    { label: "Catalogue", id: "products" },
    { label: "Brands", id: "brands" },
    { label: "Price Lists", id: "pricelists" },
    { label: "Calculator", id: "calculator" },
    { label: "Company", id: "company" },
    { label: "Enquire", id: "enquiry" },
];

export default function Footer() {
    return (
        <footer className="relative bg-inkdark text-white overflow-hidden" data-testid="footer">
            <Ticker />

            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pt-16 pb-8">
                <div className="grid md:grid-cols-12 gap-10 mb-16">
                    <div className="md:col-span-5">
                        <div className="flex items-center gap-3 mb-4">
                            <img
                                src="/brand/roxy-logo-full.png"
                                alt="Roxy Cables — Powering a Brighter Tomorrow"
                                data-testid="footer-logo"
                                className="h-16 w-auto rounded-md bg-white p-1"
                            />
                        </div>
                        <p className="text-white/50 text-sm leading-relaxed max-w-sm">
                            Every grid starts with good wire. Cables &amp; wires only — stocked deep in
                            Chickpet, Bengaluru. Dispatch timelines are confirmed on every quotation.
                        </p>
                    </div>

                    <div className="md:col-span-3">
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-4">Navigate</p>
                        <ul className="space-y-2.5">
                            {LINKS.map((l) => (
                                <li key={l.id}>
                                    <button
                                        data-testid={`footer-link-${l.id}`}
                                        onClick={() => scrollToId(l.id)}
                                        className="font-mono text-xs uppercase tracking-[0.2em] text-white/60 hover:text-copper-bright transition-colors duration-300"
                                    >
                                        {l.label}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="md:col-span-4">
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-4">Reach Us</p>
                        <div className="grid grid-cols-2 gap-2 font-mono text-xs text-white/60" data-testid="footer-offices">
                            {OFFICES.map((o) => (
                                <span key={o} className="flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 rotate-45 bg-copper-bright" /> {o}
                                </span>
                            ))}
                        </div>
                        <p className="mt-6 font-mono text-xs text-white/60">sales@roxycables.com</p>
                        <p className="font-mono text-xs text-white/60">
                            <a href="tel:+919008177176" data-testid="footer-call-mobile" className="hover:text-copper-bright transition-colors duration-300">+91 900 81 77 176</a>
                            {" · "}
                            <a href="tel:+918041689333" data-testid="footer-call-landline" className="hover:text-copper-bright transition-colors duration-300">+91 080 41689333</a>
                        </p>
                        <p className="mt-3 font-mono text-[11px] text-white/40 leading-relaxed">
                            # 80/4, Bassettepet, A M Lane, B V K Iyengar Road cross,
                            <br />
                            behind milk booth, Chickpet, Bengaluru 560053
                        </p>
                    </div>
                </div>

                <p className="font-display font-bold leading-none tracking-tight text-[12vw] md:text-[10vw] text-center text-outline-bright opacity-40 select-none" data-testid="footer-wordmark">
                    Roxy Cables
                </p>

                <div className="mt-10 pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between gap-3 font-mono text-[10px] tracking-[0.2em] uppercase text-white/40">
                    <span className="normal-case">© 2026 ROXY CABLES</span>
                    <span>Cables &amp; Wires Only.</span>
                </div>
            </div>
        </footer>
    );
}
